import { Fragment, useState } from 'react';
import { mmdd } from '../lib/format.js';

/**
 * Hand-drawn SVG bar chart, ported from the original dashboard's no-dependency
 * charts. Two modes:
 *  - 'stack':   segments stacked per day (revenue by provider), optional line
 *               overlay (attributed cost)
 *  - 'overlay': a wide dim bar with a narrower accent bar in front
 *               (reported vs monetized searches)
 *
 * Hovering a column (anywhere in its full-height band, so short bars are just
 * as reachable as tall ones) highlights it and shows `tooltip` in a styled
 * panel. That panel replaces the native SVG <title> tooltip this used to rely
 * on: <title> waits ~1s for the OS, can't be styled, and gave no indication of
 * which column you were reading. The same text stays on the group as an
 * aria-label so assistive tech still gets the per-day detail — deliberately
 * NOT as <title>, which would make the browser draw its own tooltip on top of
 * ours.
 */
export interface StackSegment {
  key: string;
  value: number;
  color: string;
}

export interface BarDatum {
  day: string;
  segments: StackSegment[];
  line?: number | null | undefined;
  tooltip: string;
}

interface Props {
  data: BarDatum[];
  mode: 'stack' | 'overlay';
  height?: number;
  formatValue: (v: number) => string;
}

const W = 640;
const PAD_L = 46;
const PAD_B = 20;
const PAD_T = 8;

export function BarChart({ data, mode, height = 220, formatValue }: Props) {
  const [hover, setHover] = useState<number | null>(null);
  const H = height;
  const plotW = W - PAD_L - 6;
  const plotH = H - PAD_B - PAD_T;
  const max = Math.max(
    1e-9,
    ...data.map((d) =>
      Math.max(
        d.segments.reduce((a, s) => a + s.value, 0),
        d.line ?? 0,
      ),
    ),
  );
  const y = (v: number) => PAD_T + plotH - (v / max) * plotH;
  const n = Math.max(1, data.length);
  const step = plotW / n;
  const barW = Math.max(2, Math.min(28, step * 0.66));
  const centre = (i: number) => PAD_L + i * step + step / 2;

  // ~4 horizontal gridlines at round-ish values.
  const ticks = [0.25, 0.5, 0.75, 1].map((f) => max * f);

  // Label every k-th day so labels never collide.
  const every = Math.ceil(n / 8);

  const linePts = data
    .map((d, i) => (d.line != null ? { i, x: centre(i), y: y(d.line) } : null))
    .filter((p): p is { i: number; x: number; y: number } => p != null);
  const linePoints = linePts.map((p) => `${p.x},${p.y}`).join(' ');

  // The tallest thing in a column — the tooltip hangs off the top of it.
  const columnTop = (d: BarDatum): number => {
    const bars =
      mode === 'stack'
        ? d.segments.reduce((a, s) => a + s.value, 0)
        : Math.max(0, ...d.segments.map((s) => s.value));
    return y(Math.max(bars, d.line ?? 0));
  };

  const hovered = hover != null ? data[hover] : undefined;
  // Percentages, not pixels: the SVG scales with the card (preserveAspectRatio
  // is 'none'), so a fraction of the viewBox is a fraction of the rendered box
  // and the panel stays put across resizes without measuring anything.
  const xFrac = hover != null ? centre(hover) / W : 0;
  const yFrac = hovered ? columnTop(hovered) / H : 0;
  // Beside the column, never over it — the bar you're reading has to stay
  // visible. Left-half columns get the panel on their right and vice versa,
  // which also keeps it inside the card at both ends.
  const anchorX = xFrac < 0.55 ? '10px' : 'calc(-100% - 10px)';
  // Anchored to the top of the column: it grows upward when there's room above
  // and downward when the bar is tall enough to fill it.
  const anchorY = yFrac < 0.45 ? '0' : '-100%';

  return (
    <div className="chart-wrap" onPointerLeave={() => setHover(null)}>
      <svg
        className="chart-svg"
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label="bar chart"
        preserveAspectRatio="none"
      >
        {ticks.map((t) => (
          <Fragment key={t}>
            <line
              x1={PAD_L}
              x2={W - 4}
              y1={y(t)}
              y2={y(t)}
              stroke="var(--gridline)"
              strokeWidth="1"
            />
            <text x={PAD_L - 6} y={y(t) + 3.5} fontSize="10" fill="var(--muted)" textAnchor="end">
              {formatValue(t)}
            </text>
          </Fragment>
        ))}
        <line x1={PAD_L} x2={W - 4} y1={y(0)} y2={y(0)} stroke="var(--border)" strokeWidth="1" />

        {data.map((d, i) => {
          const cx = centre(i);
          let acc = 0;
          return (
            <g key={d.day} role="graphics-symbol" aria-label={d.tooltip}>
              {/* behind the bars: the band that shows which column is read */}
              {hover === i ? (
                <rect
                  x={cx - step / 2}
                  y={PAD_T}
                  width={step}
                  height={plotH}
                  fill="var(--ink)"
                  className="chart-band"
                />
              ) : null}
              {mode === 'stack'
                ? d.segments.map((s) => {
                    const y0 = y(acc + s.value);
                    const h = y(acc) - y0;
                    acc += s.value;
                    return (
                      <rect
                        key={s.key}
                        x={cx - barW / 2}
                        y={y0}
                        width={barW}
                        height={Math.max(0, h)}
                        fill={s.color}
                      />
                    );
                  })
                : d.segments.map((s, si) => {
                    const w = si === 0 ? barW : barW * 0.55;
                    return (
                      <rect
                        key={s.key}
                        x={cx - w / 2}
                        y={y(s.value)}
                        width={w}
                        height={Math.max(0, y(0) - y(s.value))}
                        fill={s.color}
                      />
                    );
                  })}
              {i % every === 0 ? (
                <text x={cx} y={H - 6} fontSize="10" fill="var(--muted)" textAnchor="middle">
                  {mmdd(d.day)}
                </text>
              ) : null}
              {/* full-height hover target, last so it sits above the bars */}
              <rect
                x={cx - step / 2}
                y={PAD_T}
                width={step}
                height={plotH}
                fill="transparent"
                data-day={d.day}
                onPointerEnter={() => setHover(i)}
              />
            </g>
          );
        })}

        {mode === 'stack' && linePoints ? (
          <>
            <polyline
              points={linePoints}
              fill="none"
              stroke="var(--gold)"
              strokeWidth="1.5"
              strokeDasharray="4 3"
            />
            {/* the line's own points, so a single day's cost is readable too */}
            {linePts.map((p) => (
              <circle
                key={p.i}
                cx={p.x}
                cy={p.y}
                r={hover === p.i ? 4 : 2}
                fill="var(--gold)"
                pointerEvents="none"
              />
            ))}
          </>
        ) : null}
      </svg>

      {hovered ? (
        <div
          className="chart-tip"
          role="status"
          style={{
            left: `${xFrac * 100}%`,
            top: `${yFrac * 100}%`,
            transform: `translate(${anchorX}, ${anchorY})`,
          }}
        >
          {hovered.tooltip}
        </div>
      ) : null}
    </div>
  );
}
